import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { environment } from '@env/environment';
import Lara from '@primeng/themes/lara';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { providePrimeNG } from 'primeng/config';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';

import { CustomTranslateLoader } from '@src/app/shared/loaders/custom-translate.loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CustomCompaniesHeaderInterceptor } from './core/interceptors/custom-companies-header.interceptor';
import { LongQueryToPostInterceptor } from './core/interceptors/long-query-to-post.interceptor';
import { GlobalErrorHandler } from './core/services/helpers/global-error-handler.service';
import { CreatePasswordModule } from './features/create-password/create-password.module';
import { DashboardModule } from './features/dashboard/dashboard.module';
import { LoginModule } from './features/login/login.module';
import { OnboardingModule } from './features/onboarding/onboarding.module';
import { AiChatGlobalOverlayComponent } from './shared/components/ai-chat-global-overlay/ai-chat-global-overlay.component';
import { FloatingFeedbackButtonComponent } from './shared/components/floating-feedback-button/floating-feedback-button.component';
import { SharedModule } from './shared/shared.module';
import { reducers } from './state/index';
import { localStorageSyncReducer } from './state/localStorageSyncReducer';
import { MonitoringEffects } from './state/monitoring/monitoring.effects';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: () => new CustomTranslateLoader(),
      },
    }),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    NgxMaskDirective,
    NgxMaskPipe,
    ConfirmDialogModule,
    ToastModule,
    SharedModule,
    LoginModule,
    DashboardModule,
    OnboardingModule,
    CreatePasswordModule,
    MessageModule,
    EffectsModule.forRoot([MonitoringEffects]),
    ButtonModule,
    DynamicDialogModule,
    AiChatGlobalOverlayComponent,
    FloatingFeedbackButtonComponent,
  ],
  providers: [
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideEnvironmentNgxMask(),
    // MessageService fica provido SÓ aqui (injetor raiz). O `<p-toast>` do app.component e o
    // ToastHelperService (providedIn: 'root') precisam da MESMA instância, senão o toast não
    // renderiza. Ver "Toast: uma instância só de MessageService" no AGENTS.md do frontend.
    MessageService,
    ConfirmationService,
    DialogService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomCompaniesHeaderInterceptor,
      multi: true,
    },
    // Depois do de escopo, de propósito: interceptors rodam na ordem em que são providos, e este precisa ver
    // a requisição final. GET da API com querystring longa (lista de ids) vira POST com a query no corpo e
    // `X-Http-Method-Override: GET` (card 3571; README em core/interceptors).
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LongQueryToPostInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: false, // Desabilita dark mode - sempre usa light theme
        },
      },
      translation: {
        firstDayOfWeek: 0,
        dayNames: [
          'Domingo',
          'Segunda-feira',
          'Terça-feira',
          'Quarta-feira',
          'Quinta-feira',
          'Sexta-feira',
          'Sábado',
        ],
        dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
        dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
        monthNames: [
          'Janeiro',
          'Fevereiro',
          'Março',
          'Abril',
          'Maio',
          'Junho',
          'Julho',
          'Agosto',
          'Setembro',
          'Outubro',
          'Novembro',
          'Dezembro',
        ],
        monthNamesShort: [
          'Jan',
          'Fev',
          'Mar',
          'Abr',
          'Mai',
          'Jun',
          'Jul',
          'Ago',
          'Set',
          'Out',
          'Nov',
          'Dez',
        ],
        today: 'Hoje',
        clear: 'Limpar',
        weekHeader: 'Sem',
      },
    }),
  ],
})
export class AppModule {}
